import React, { useEffect, useRef, useState } from 'react'
import { FastForward } from 'lucide-react'
import useCasesStore from '../store/useCasesStore'
import useAudioManager from '../hooks/useAudioManager'

const ELITE_ITEM_MIN_PRICE = 7

const isEliteItem = (item) => item?.price >= ELITE_ITEM_MIN_PRICE

const distributeRouletteSkins = (skins) => {
  if (!Array.isArray(skins) || skins.length <= 1) {
    return Array.isArray(skins) ? [...skins] : []
  }

  const eliteSkins = skins.filter((item) => isEliteItem(item))
  const regularSkins = skins.filter((item) => !isEliteItem(item))

  if (eliteSkins.length === 0 || regularSkins.length === 0) {
    return [...skins]
  }

  const distributedItems = [...regularSkins]

  eliteSkins.forEach((skin, index) => {
    const insertIndex = Math.min(
      distributedItems.length,
      Math.floor(((index + 1) * (regularSkins.length + 1)) / (eliteSkins.length + 1)) + index
    )

    distributedItems.splice(insertIndex, 0, skin)
  })

  return distributedItems
}

// Константы для управления рулеткой
const TOTAL_ITEMS = 100        // Фиксированное количество скинов в рулетке
const WINNING_POSITION = 90   // Позиция выигрышного скина (от начала)

// Константы для звука и вибрации
const ANIMATION_DURATION = 9000  // Длительность анимации в миллисекундах
const ITEM_HEIGHT = 96           // Высота одного элемента в пикселях
const INITIAL_TICK_INTERVAL = 50 // Начальный интервал между тиками (мс)
const FINAL_TICK_INTERVAL = 400  // Конечный интервал между тиками (мс)

const CaseRoulette = ({ caseId, startAnimation = false, onAnimationComplete, onSkip, canSkip = false }) => {
  const rouletteRef = useRef(null)
  const tickIntervalRef = useRef(null)
  const animationTimeoutRef = useRef(null) // Ref для хранения timeout ID анимации
  const isStoppedRef = useRef(false) // Флаг остановки анимации для блокировки вибрации
  const [items, setItems] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [animationStarted, setAnimationStarted] = useState(false)
  const [isSkipping, setIsSkipping] = useState(false) // Флаг процесса пропуска
  const { currentCase, openingData } = useCasesStore()
  
  // Инициализация Audio Manager через глобальный audioService
  const { playSound, stopSound } = useAudioManager({
    spin: 'rouletteSpin'
  })

  // Очистка таймеров и звуков при размонтировании
  useEffect(() => {
    return () => {
      // Очищаем интервал тиков
      if (tickIntervalRef.current) {
        clearTimeout(tickIntervalRef.current)
        tickIntervalRef.current = null
      }
      // Очищаем timeout анимации
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
        animationTimeoutRef.current = null
      }
      // Останавливаем звук рулетки при размонтировании
      stopSound('spin')
    }
  }, []) // Пустой массив - cleanup только при размонтировании

  // Функция для расчета интервала тика на основе времени анимации
  const calculateTickInterval = (elapsedTime) => {
    // Нормализуем время от 0 до 1
    const progress = Math.min(elapsedTime / ANIMATION_DURATION, 1)
    
    // Применяем cubic-bezier(.01,0,.3,1) для расчета скорости
    // Упрощенная аппроксимация cubic-bezier функции
    const easedProgress = progress < 0.8 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 3) / 2
    
    // Интерполируем интервал от начального к конечному
    const interval = INITIAL_TICK_INTERVAL + 
      (FINAL_TICK_INTERVAL - INITIAL_TICK_INTERVAL) * easedProgress
    
    return Math.round(interval)
  }

  // Функция вибрации (без звука)
  const triggerVibration = () => {
    // Проверяем флаг остановки - если анимация остановлена, не вызываем вибрацию
    if (isStoppedRef.current) return
    
    try {
      // Вызываем вибрацию через Telegram WebApp API
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
      }
    } catch (error) {
      // Игнорируем ошибки вибрации
    }
  }

  // Функция запуска вибрационной анимации с переменными интервалами
  const startVibrationAnimation = () => {
    const startTime = Date.now()
    
    const scheduleNextVibration = () => {
      const elapsedTime = Date.now() - startTime
      
      // Останавливаем анимацию по истечении времени
      if (elapsedTime >= ANIMATION_DURATION) {
        if (tickIntervalRef.current) {
          clearTimeout(tickIntervalRef.current)
          tickIntervalRef.current = null
        }
        return
      }
      
      // Вызываем вибрацию
      triggerVibration()
      
      // Рассчитываем следующий интервал
      const nextInterval = calculateTickInterval(elapsedTime)
      
      // Планируем следующую вибрацию
      tickIntervalRef.current = setTimeout(scheduleNextVibration, nextInterval)
    }
    
    // Запускаем первую вибрацию сразу
    scheduleNextVibration()
  }

  // Функция остановки вибрации
  const stopVibrationAnimation = () => {
    // Устанавливаем флаг остановки ПЕРВЫМ делом - блокирует новые вызовы вибрации
    isStoppedRef.current = true
    
    // Останавливаем интервал вибраций
    if (tickIntervalRef.current) {
      clearTimeout(tickIntervalRef.current)
      tickIntervalRef.current = null
    }
    
    // Принудительно останавливаем любую активную вибрацию через Telegram API
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        // Вызываем селекцию для "сброса" состояния вибрации
        window.Telegram.WebApp.HapticFeedback.selectionChanged()
      }
    } catch (error) {
      // Игнорируем ошибки
    }
    
    // Останавливаем timeout завершения анимации
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = null
    }
  }

  // Обработчик нажатия кнопки пропуска
  const handleSkipClick = () => {
    if (isSkipping || !canSkip) return // Защита от повторных нажатий
    
    setIsSkipping(true) // Блокируем кнопку
    stopVibrationAnimation() // Останавливаем вибрацию
    stopSound('spin') // Останавливаем звук рулетки с плавным затуханием
    
    if (onSkip) {
      onSkip() // Вызываем callback из родительского компонента
    }
  }

  useEffect(() => {
    // Get skins from current case (loaded via API)
    const skins = currentCase?.skins || []
    if (skins.length === 0) return

    // При смене кейса/скинов переинициализируем рулетку
    const extendedItems = []
    const rouletteSkins = distributeRouletteSkins(skins)

    // Заполняем рулетку до нужного количества (используем индекс вместо random для стабильности)
    for (let i = 0; i < TOTAL_ITEMS; i++) {
      const skinIndex = i % rouletteSkins.length
      extendedItems.push(rouletteSkins[skinIndex])
    }

    setItems(extendedItems)
    setIsInitialized(true)
    setAnimationStarted(false)
  }, [currentCase])

  useEffect(() => {
    // Обновляем только выигрышный скин, когда приходят данные от API (или догружаются позже)
    const skins = currentCase?.skins || []
    if (!openingData?.win) return
    if (!isInitialized) return
    if (skins.length === 0) return

    setItems((prevItems) => {
      if (!prevItems || prevItems.length === 0) return prevItems

      const updatedItems = [...prevItems]
      updatedItems[WINNING_POSITION] = openingData.win

      // Near miss эффект: с вероятностью 20% размещаем элитный скин рядом
      const shouldShowNearMiss = Math.random() < 0.20 // 1 из 5

      if (shouldShowNearMiss) {
        const eliteSkins = skins.filter((skin) => isEliteItem(skin))

        if (eliteSkins.length > 0) {
          const randomEliteSkin = eliteSkins[Math.floor(Math.random() * eliteSkins.length)]
          const nearMissPosition = Math.random() < 0.5 ? WINNING_POSITION - 1 : WINNING_POSITION + 1
          updatedItems[nearMissPosition] = randomEliteSkin
        }
      }

      return updatedItems
    })
  }, [openingData, isInitialized, currentCase])

  useEffect(() => {
    // Start roulette animation only when startAnimation is true
    if (!startAnimation) return
    if (animationStarted) return
    if (items.length === 0) return

    setAnimationStarted(true)

    // Сбрасываем флаг остановки при запуске новой анимации
    isStoppedRef.current = false

    // Запускаем звуковую дорожку один раз в начале
    playSound('spin')

    // Запускаем вибрационную анимацию с переменными интервалами
    startVibrationAnimation()

    setTimeout(() => {
      if (rouletteRef.current) {
        // Calculate final position (stop at winning item in center of viewport)
        const itemHeight = 96 // высота одного элемента в пикселях
        const containerHeight = 576 // высота контейнера рулетки (h-96 = 384px)
        const centerOffset = containerHeight / 2 - itemHeight / 2 // смещение для центрирования

        // Добавляем случайное смещение в пределах размера элемента
        const randomOffset = (Math.random() - 0.5) * 84
        const finalPosition = WINNING_POSITION * itemHeight - centerOffset + randomOffset

        rouletteRef.current.style.transform = `translateY(-${finalPosition}px)`
      }
    }, 0)

    // Вызываем callback после завершения анимации (10000ms transition + 100ms запас + 1000ms пауза)
    animationTimeoutRef.current = setTimeout(() => {
      if (onAnimationComplete) {
        onAnimationComplete()
      }
    }, 11200) // 10 сек анимация + 100ms запас + 1 сек пауза на целевом скине
  }, [startAnimation, animationStarted, items.length, onAnimationComplete])

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Roulette Container */}
      <div className="relative overflow-hidden rounded-lg border-2 border-accent-primary/50 bg-surface h-[576px]">
        {/* Selection Indicator */}
        <div className="absolute top-1/2 left-0 right-0 transform -translate-y-1/2 z-10">
          <div className="h-1 w-full bg-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.8)]"></div>
          <div className="absolute top-1/2 -left-2 transform -translate-y-1/2">
            <div className="w-0 h-0 border-t-4 border-b-4 border-r-8 border-t-transparent border-b-transparent border-r-accent-primary"></div>
          </div>
          <div className="absolute top-1/2 -right-2 transform -translate-y-1/2">
            <div className="w-0 h-0 border-t-4 border-b-4 border-l-8 border-t-transparent border-b-transparent border-l-accent-primary"></div>
          </div>
        </div>

        {/* Items Roulette - Vertical */}
        <div 
          ref={rouletteRef}
          className="flex flex-col"
          style={{ 
            transform: 'translateY(0px)',
            transition: 'transform 10500ms cubic-bezier(.01,0,.3,1)',
          }}
        >
          {items.map((item, index) => {
            const isElite = isEliteItem(item)
            return (
              <div 
                key={index}
                className={`flex-shrink-0 w-full h-24 border-b flex items-center justify-center p-2 relative ${
                  isElite 
                    ? 'border-yellow-500/20 bg-gradient-to-r from-yellow-500/15 via-yellow-400/10 to-yellow-500/5' 
                    : 'border-white border-opacity-10 bg-surface'
                }`}
              >
                {/* Тонкое элитное свечение */}
                {isElite && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/8 to-transparent animate-pulse"></div>
                )}
                
                <div className="text-center relative z-10">
                  <img 
                    src={item.img}
                    className={`w-24 h-24 object-contain mx-auto mb-1 ${
                      isElite ? 'drop-shadow-[0_0_6px_rgba(250,204,21,0.4)]' : ''
                    }`}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>



      {/* Glow Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-accent-primary/10 to-transparent animate-pulse"></div>
      </div>

      {/* Кнопка пропуска анимации */}
      {(() => {
        console.log('🔍 CaseRoulette canSkip проверка:', { canSkip, isSkipping })
        return canSkip && !isSkipping ? (
          <div className="fixed bottom-8 left-0 right-0 flex justify-center z-50 pointer-events-auto">
            <button
              onClick={handleSkipClick}
              disabled={isSkipping}
              className="px-6 py-3 bg-surface/90 backdrop-blur-sm border-2 border-text-secondary/50 text-text-secondary rounded-lg hover:border-accent-primary/50 hover:text-accent-primary transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl"
            >
              <FastForward size={24} />
            </button>
          </div>
        ) : null
      })()}
    </div>
  )
}

export default CaseRoulette
